import React from "react";
import { graphql } from "gatsby";
import { getComponentSettings } from "../../../lib/componentSettings";
import {
  KontentComponentLayout,
  KontentComponentSettings,
  KontentMultiChoice,
  StringValue,
  YesNo,
} from "../../../types";
import { Embed } from "../Embed/Embed";
import { getMultiChoice } from "../../../lib/multichoice";

interface HoursOfOperation {
  elements: {
    monday_open_time: StringValue;
    monday_close_time: StringValue;
    tuesday_open_time: StringValue;
    tuesday_close_time: StringValue;
    wednesday_open_time: StringValue;
    wednesday_close_time: StringValue;
    thursday_open_time: StringValue;
    thursday_close_time: StringValue;
    friday_open_time: StringValue;
    friday_close_time: StringValue;
    saturday_open_time: StringValue;
    saturday_close_time: StringValue;
    sunday_open_time: StringValue;
    sunday_close_time: StringValue;
    custom_hours: {
      value: CustomHours[];
    };
  };
}

interface CustomHours {
  elements: {
    date: StringValue;
    start_time?: StringValue;
    close_time?: StringValue;
    recurring?: KontentMultiChoice<YesNo>;
  };
}
interface Props extends KontentComponentSettings, KontentComponentLayout {
  open_hours_embed_code: StringValue;
  closed_hours_embed_code: StringValue;
  hours_of_operation: {
    value: HoursOfOperation[];
  };
}

type TodayHour = {
  open: Date | null;
  close: Date | null;
  order?: number;
  name?: string;
};

const getTodaysHours = (
  hours: HoursOfOperation["elements"],
  today: Date
): TodayHour | null => {
  let todaysHours: TodayHour | null = null;

  hours.custom_hours?.value.forEach((customHour) => {
    if (todaysHours) return;

    const customDate = new Date(customHour.elements.date.value);

    if (
      today.getDate() === customDate.getDate() &&
      today.getMonth() === customDate.getMonth()
    ) {
      if (
        (customHour.elements.recurring &&
          getMultiChoice(customHour.elements.recurring) === "yes") ||
        today.getFullYear() === customDate.getFullYear()
      ) {
        todaysHours = {
          open: customHour.elements.start_time?.value
            ? new Date(customHour.elements.start_time?.value)
            : null,
          close: customHour.elements.close_time?.value
            ? new Date(customHour.elements.close_time?.value)
            : null,
        };
      }
    }
  });

  if (!todaysHours) {
    const days = createDaysArray(hours);
    const currentWeekdayHours = days.find(
      (day) => day.order === today.getDay()
    );
    todaysHours = currentWeekdayHours ? currentWeekdayHours : null;
  }

  if (todaysHours?.open) {
    todaysHours?.open.setDate(today.getDate());
    todaysHours?.open.setMonth(today.getMonth());
    todaysHours?.open.setFullYear(today.getFullYear());
  }

  if (todaysHours?.close) {
    todaysHours.close.setDate(today.getDate());
    todaysHours?.close.setMonth(today.getMonth());
    todaysHours?.close.setFullYear(today.getFullYear());
  }

  return todaysHours;
};
const createDaysArray = (hours: HoursOfOperation["elements"]) => [
  {
    name: "Sunday",
    order: 0,
    open: hours.sunday_open_time?.value
      ? new Date(hours.sunday_open_time?.value)
      : null,
    close: hours.sunday_close_time?.value
      ? new Date(hours.sunday_close_time?.value)
      : null,
  },
  {
    name: "Monday",
    order: 1,
    open: hours.monday_open_time?.value
      ? new Date(hours.monday_open_time?.value)
      : null,
    close: hours.monday_close_time?.value
      ? new Date(hours.monday_close_time?.value)
      : null,
  },
  {
    name: "Tuesday",
    order: 2,
    open: hours.tuesday_open_time?.value
      ? new Date(hours.tuesday_open_time?.value)
      : null,
    close: hours.tuesday_close_time?.value
      ? new Date(hours.tuesday_close_time?.value)
      : null,
  },
  {
    name: "Wednesday",
    order: 3,
    open: hours.wednesday_open_time?.value
      ? new Date(hours.wednesday_open_time?.value)
      : null,
    close: hours.wednesday_close_time?.value
      ? new Date(hours.wednesday_close_time?.value)
      : null,
  },
  {
    name: "Thursday",
    order: 4,
    open: hours.thursday_open_time?.value
      ? new Date(hours.thursday_open_time?.value)
      : null,
    close: hours.thursday_close_time?.value
      ? new Date(hours.thursday_close_time?.value)
      : null,
  },
  {
    name: "Friday",
    order: 5,
    open: hours.friday_open_time?.value
      ? new Date(hours.friday_open_time?.value)
      : null,
    close: hours.friday_close_time?.value
      ? new Date(hours.friday_close_time?.value)
      : null,
  },
  {
    name: "Saturday",
    order: 6,
    open: hours.saturday_open_time?.value
      ? new Date(hours.saturday_open_time?.value)
      : null,
    close: hours.saturday_close_time?.value
      ? new Date(hours.saturday_close_time?.value)
      : null,
  },
];

const TimeSensitiveEmbedContainer: React.FC<Props> = ({
  open_hours_embed_code,
  closed_hours_embed_code,
  hours_of_operation,
  ...props
}) => {
  const componentSettings = getComponentSettings({ ...props });
  const hours = hours_of_operation?.value[0].elements;
  const today = new Date();

  const todaysHours = getTodaysHours(hours, today);

  if (
    (!todaysHours?.open && !todaysHours?.close) ||
    (todaysHours.open && today.getTime() < todaysHours?.open?.getTime()) ||
    (todaysHours.close && today.getTime() > todaysHours?.close?.getTime())
  ) {
    return (
      <Embed
        code={closed_hours_embed_code?.value}
        {...componentSettings}
      ></Embed>
    );
  }

  return (
    <Embed code={open_hours_embed_code?.value} {...componentSettings}></Embed>
  );
};

export default TimeSensitiveEmbedContainer;

export const fragmentTimeSensitiveEmbedContainer = graphql`
  fragment TimeSensitiveEmbed on kontent_item_time_sensitive_embed {
    system {
      id
      codename
    }
    internal {
      type
      contentDigest
    }
    elements {
      open_hours_embed_code {
        value
      }
      closed_hours_embed_code {
        value
      }
      component_settings__hide_on_mobile {
        value {
          codename
        }
      }
      component_settings__anchor_name {
        value
      }
      component_layout__component_width {
        value {
          codename
        }
      }
      hours_of_operation {
        value {
          internal {
            type
            contentDigest
          }
          ... on kontent_item_hours_of_operation {
            id
            elements {
              monday_open_time {
                value
              }
              monday_close_time {
                value
              }
              tuesday_open_time {
                value
              }
              tuesday_close_time {
                value
              }
              wednesday_open_time {
                value
              }
              wednesday_close_time {
                value
              }
              thursday_open_time {
                value
              }
              thursday_close_time {
                value
              }
              friday_open_time {
                value
              }
              friday_close_time {
                value
              }
              saturday_open_time {
                value
              }
              saturday_close_time {
                value
              }
              sunday_open_time {
                value
              }
              sunday_close_time {
                value
              }
              custom_hours {
                value {
                  ... on kontent_item_custom_hours {
                    id
                    internal {
                      type
                      contentDigest
                    }
                    elements {
                      date {
                        value
                      }
                      start_time {
                        value
                      }
                      recurring {
                        value {
                          codename
                        }
                      }
                      close_time {
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
